import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { DatePeriod } from '@/components/dashboard/filters'
import { useDateFilter } from './useDateFilter'

// Local types for Supabase query results
interface RevenueEventWithSession {
  id: string
  visitor_hash: string
  amount_cents: number
  timestamp: string
  sessions: {
    visitor_hash: string
    device_type: string | null
    os: string | null
    country_code: string | null
    browser: string | null
    referrer: string | null
    started_at: string
  } | null
}

interface SessionRow {
  visitor_hash: string
  started_at: string
  pageview_count: number
}

interface EventRow {
  event_type: string
  visitor_hash: string
}

export interface RevenueByCategory {
  label: string
  revenue: number
  percentage: number
}

export interface TimeToConvertBucket {
  label: string
  count: number
  percentage: number
}

export interface ConversionHeatmapCell {
  dayOfWeek: number // 0 = Sunday
  hour: number // 0-23
  conversions: number
}

export interface TopEvent {
  eventName: string
  count: number
  conversionRate: number
  revenue: number
}

export interface RevenueGrowthPoint {
  date: string
  revenuePerVisitor: number
}

export interface InsightsData {
  // Revenue breakdowns
  revenueByDevice: RevenueByCategory[]
  revenueByOS: RevenueByCategory[]
  revenueByCountry: RevenueByCategory[]
  revenueBySource: RevenueByCategory[]

  // Conversion journey metrics
  daysToConvert: TimeToConvertBucket[]
  visitsToConvert: TimeToConvertBucket[]

  // Growth over time
  revenueGrowth: RevenueGrowthPoint[]

  // Top events
  topEvents: TopEvent[]

  // Conversion heatmap
  conversionHeatmap: ConversionHeatmapCell[]

  // Summary stats
  totalRevenue: number
  totalConversions: number
  avgDaysToConvert: number
  avgVisitsToConvert: number

  isLoading: boolean
  error: Error | null
}

export function useInsightsData(
  siteId: string | undefined,
  period: DatePeriod = 'last30d'
): InsightsData {
  const dateFilter = useDateFilter(period, 'daily')

  const [data, setData] = useState<Omit<InsightsData, 'isLoading' | 'error'>>({
    revenueByDevice: [],
    revenueByOS: [],
    revenueByCountry: [],
    revenueBySource: [],
    daysToConvert: [],
    visitsToConvert: [],
    revenueGrowth: [],
    topEvents: [],
    conversionHeatmap: [],
    totalRevenue: 0,
    totalConversions: 0,
    avgDaysToConvert: 0,
    avgVisitsToConvert: 0,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!siteId) return

    try {
      setError(null)

      // Fetch revenue events with session data
      const { data: revenueData, error: revenueError } = await supabase
        .from('revenue_events')
        .select(`
          *,
          sessions!inner (
            visitor_hash,
            device_type,
            os,
            country_code,
            browser,
            referrer,
            started_at
          )
        `)
        .eq('site_id', siteId)
        .gte('timestamp', dateFilter.startDateISO)
        .lte('timestamp', dateFilter.endDateISO)
        .order('timestamp', { ascending: true })

      if (revenueError) throw revenueError

      const revenue = (revenueData || []) as RevenueEventWithSession[]

      // Fetch sessions for visitor journey analysis
      const { data: sessionsData } = await supabase
        .from('sessions')
        .select('visitor_hash, started_at, pageview_count')
        .eq('site_id', siteId)
        .eq('is_bot', false)
        .order('started_at', { ascending: true })
        .limit(2000)

      const sessions = (sessionsData || []) as SessionRow[]

      // Fetch events for top events analysis
      const { data: eventsData } = await supabase
        .from('events')
        .select('event_type, visitor_hash')
        .eq('site_id', siteId)
        .gte('timestamp', dateFilter.startDateISO)
        .lte('timestamp', dateFilter.endDateISO)
        .not('event_type', 'eq', 'pageview')
        .limit(5000)

      const events = (eventsData || []) as EventRow[]

      // Calculate total revenue and conversions
      const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount_cents || 0), 0) / 100
      const totalConversions = revenue.length

      // Track converted visitor hashes
      const convertedVisitors = new Set(revenue.map(r => r.visitor_hash))

      // Calculate revenue by device
      const deviceRevenue = new Map<string, number>()
      for (const r of revenue) {
        const device = r.sessions?.device_type || 'Unknown'
        deviceRevenue.set(device, (deviceRevenue.get(device) || 0) + (r.amount_cents || 0) / 100)
      }
      const revenueByDevice = Array.from(deviceRevenue.entries())
        .map(([label, revenue]) => ({
          label: label.charAt(0).toUpperCase() + label.slice(1),
          revenue,
          percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)

      // Calculate revenue by OS
      const osRevenue = new Map<string, number>()
      for (const r of revenue) {
        const os = r.sessions?.os || 'Unknown'
        osRevenue.set(os, (osRevenue.get(os) || 0) + (r.amount_cents || 0) / 100)
      }
      const revenueByOS = Array.from(osRevenue.entries())
        .map(([label, revenue]) => ({
          label,
          revenue,
          percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)

      // Calculate revenue by country
      const countryRevenue = new Map<string, number>()
      for (const r of revenue) {
        const country = r.sessions?.country_code || 'Unknown'
        countryRevenue.set(country, (countryRevenue.get(country) || 0) + (r.amount_cents || 0) / 100)
      }
      const revenueByCountry = Array.from(countryRevenue.entries())
        .map(([label, revenue]) => ({
          label,
          revenue,
          percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)

      // Calculate revenue by source
      const sourceRevenue = new Map<string, number>()
      for (const r of revenue) {
        let source = 'Direct'
        const referrer = r.sessions?.referrer
        if (referrer) {
          try {
            const url = new URL(referrer)
            source = url.hostname.replace('www.', '')
          } catch {
            source = referrer
          }
        }
        sourceRevenue.set(source, (sourceRevenue.get(source) || 0) + (r.amount_cents || 0) / 100)
      }
      const revenueBySource = Array.from(sourceRevenue.entries())
        .map(([label, revenue]) => ({
          label,
          revenue,
          percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)

      // Calculate days to convert and visits to convert
      const firstVisitByVisitor = new Map<string, Date>()
      const visitCountByVisitor = new Map<string, number>()

      for (const session of sessions) {
        const startedAt = new Date(session.started_at)
        const existing = firstVisitByVisitor.get(session.visitor_hash)
        if (!existing || startedAt < existing) {
          firstVisitByVisitor.set(session.visitor_hash, startedAt)
        }
        visitCountByVisitor.set(
          session.visitor_hash,
          (visitCountByVisitor.get(session.visitor_hash) || 0) + 1
        )
      }

      const daysToConvertValues: number[] = []
      const visitsToConvertValues: number[] = []

      for (const r of revenue) {
        const firstVisit = firstVisitByVisitor.get(r.visitor_hash)
        const visitCount = visitCountByVisitor.get(r.visitor_hash)

        if (firstVisit) {
          const conversionDate = new Date(r.timestamp)
          const daysDiff = Math.max(0, Math.floor((conversionDate.getTime() - firstVisit.getTime()) / (1000 * 60 * 60 * 24)))
          daysToConvertValues.push(daysDiff)
        }

        if (visitCount) {
          visitsToConvertValues.push(visitCount)
        }
      }

      // Bucket days to convert
      const daysBuckets = [
        { min: 0, max: 0, label: 'Same day' },
        { min: 1, max: 1, label: '1 day' },
        { min: 2, max: 3, label: '2-3 days' },
        { min: 4, max: 7, label: '4-7 days' },
        { min: 8, max: 14, label: '1-2 weeks' },
        { min: 15, max: 30, label: '2-4 weeks' },
        { min: 31, max: Infinity, label: '1+ month' },
      ]

      const daysToConvert = daysBuckets.map(bucket => {
        const count = daysToConvertValues.filter(d => d >= bucket.min && d <= bucket.max).length
        return {
          label: bucket.label,
          count,
          percentage: daysToConvertValues.length > 0 ? (count / daysToConvertValues.length) * 100 : 0,
        }
      })

      // Bucket visits to convert
      const visitsBuckets = [
        { min: 1, max: 1, label: '1 visit' },
        { min: 2, max: 2, label: '2 visits' },
        { min: 3, max: 3, label: '3 visits' },
        { min: 4, max: 5, label: '4-5 visits' },
        { min: 6, max: 10, label: '6-10 visits' },
        { min: 11, max: Infinity, label: '10+ visits' },
      ]

      const visitsToConvert = visitsBuckets.map(bucket => {
        const count = visitsToConvertValues.filter(v => v >= bucket.min && v <= bucket.max).length
        return {
          label: bucket.label,
          count,
          percentage: visitsToConvertValues.length > 0 ? (count / visitsToConvertValues.length) * 100 : 0,
        }
      })

      // Calculate averages
      const avgDaysToConvert = daysToConvertValues.length > 0
        ? daysToConvertValues.reduce((a, b) => a + b, 0) / daysToConvertValues.length
        : 0

      const avgVisitsToConvert = visitsToConvertValues.length > 0
        ? visitsToConvertValues.reduce((a, b) => a + b, 0) / visitsToConvertValues.length
        : 0

      // Calculate revenue growth over time (revenue per visitor by date)
      const visitorsByDate = new Map<string, Set<string>>()
      const revenueByDate = new Map<string, number>()

      for (const session of sessions) {
        const date = session.started_at.split('T')[0]
        if (!visitorsByDate.has(date)) {
          visitorsByDate.set(date, new Set())
        }
        visitorsByDate.get(date)!.add(session.visitor_hash)
      }

      for (const r of revenue) {
        const date = r.timestamp.split('T')[0]
        revenueByDate.set(date, (revenueByDate.get(date) || 0) + (r.amount_cents || 0) / 100)
      }

      const revenueGrowth: RevenueGrowthPoint[] = dateFilter.dateLabels.map(date => {
        const visitors = visitorsByDate.get(date)?.size || 0
        const dayRevenue = revenueByDate.get(date) || 0
        return {
          date,
          revenuePerVisitor: visitors > 0 ? dayRevenue / visitors : 0,
        }
      })

      // Calculate top converting events
      const eventCounts = new Map<string, { count: number; converters: Set<string> }>()
      for (const event of events) {
        if (!eventCounts.has(event.event_type)) {
          eventCounts.set(event.event_type, { count: 0, converters: new Set() })
        }
        const entry = eventCounts.get(event.event_type)!
        entry.count++
        if (convertedVisitors.has(event.visitor_hash)) {
          entry.converters.add(event.visitor_hash)
        }
      }

      const topEvents: TopEvent[] = Array.from(eventCounts.entries())
        .map(([eventName, { count, converters }]) => ({
          eventName,
          count,
          conversionRate: count > 0 ? (converters.size / count) * 100 : 0,
          revenue: 0, // Would need to correlate with revenue events
        }))
        .sort((a, b) => b.conversionRate - a.conversionRate)
        .slice(0, 10)

      // Calculate conversion heatmap (hour x day of week)
      const heatmapData: ConversionHeatmapCell[] = []
      const heatmapMap = new Map<string, number>()

      for (const r of revenue) {
        const date = new Date(r.timestamp)
        const dayOfWeek = date.getDay()
        const hour = date.getHours()
        const key = `${dayOfWeek}-${hour}`
        heatmapMap.set(key, (heatmapMap.get(key) || 0) + 1)
      }

      for (let day = 0; day < 7; day++) {
        for (let hour = 0; hour < 24; hour++) {
          const key = `${day}-${hour}`
          heatmapData.push({
            dayOfWeek: day,
            hour,
            conversions: heatmapMap.get(key) || 0,
          })
        }
      }

      setData({
        revenueByDevice,
        revenueByOS,
        revenueByCountry,
        revenueBySource,
        daysToConvert,
        visitsToConvert,
        revenueGrowth,
        topEvents,
        conversionHeatmap: heatmapData,
        totalRevenue,
        totalConversions,
        avgDaysToConvert,
        avgVisitsToConvert,
      })
    } catch (err) {
      console.error('Failed to fetch insights data:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch data'))
    } finally {
      setIsLoading(false)
    }
  }, [siteId, dateFilter])

  useEffect(() => {
    setIsLoading(true)
    fetchData()
  }, [fetchData])

  return {
    ...data,
    isLoading,
    error,
  }
}
